/**
 * MIDIEventHandler - Centralized MIDI Event Processing
 * Parses raw MIDI messages and routes CC/Note/Clock events.
 * Decoupled from app - uses state and callbacks.
 */
import { MIDI_CONSTANTS } from './MIDIConstants.js';

export class MIDIEventHandler {
  constructor(state) {
    this.state = state;
    this.ccHandlers = new Map();
    this.noteHandlers = new Map();
    this.learnCCListeners = new Set();
    this.learnNoteListeners = new Set();

    // Callbacks for clock/transport messages
    this.onClock = null;
    this.onStart = null;
    this.onStop = null;
    this.onContinue = null;
  }

  /**
   * Process a raw MIDI message (called by MIDIManager.onMessage)
   */
  handleMessage(data) {
    const rawStatus = data[0];
    const channel = data[0] & 0x0f;

    // System real-time messages (0xF8-0xFF)
    if (rawStatus >= 0xF8) {
      switch (rawStatus) {
        case MIDI_CONSTANTS.messageTypes.midiClock:
          this.onClock?.();
          break;
        case MIDI_CONSTANTS.messageTypes.midiStart:
          this.onStart?.();
          break;
        case MIDI_CONSTANTS.messageTypes.midiContinue:
          this.onContinue?.();
          break;
        case MIDI_CONSTANTS.messageTypes.midiStop:
          this.onStop?.();
          break;
      }
      return;
    }

    // System common messages (0xF0-0xF7) - ignore for now
    if (rawStatus >= 0xF0) return;

    // Channel messages
    const status = rawStatus & 0xf0;

    switch (status) {
      case MIDI_CONSTANTS.messageTypes.noteOff: {
        const note = data[1];
        const velocity = data[2];
        this._fireNote(note, velocity, false, channel);
        break;
      }
      case MIDI_CONSTANTS.messageTypes.noteOn: {
        const note = data[1];
        const velocity = data[2];
        const isNoteOn = velocity > 0;
        this._fireNote(note, velocity, isNoteOn, channel);
        break;
      }
      case MIDI_CONSTANTS.messageTypes.controlChange: {
        const controller = data[1];
        const value = data[2];
        this._fireCC(controller, value, channel);
        break;
      }
    }
  }

  _fireCC(controller, value, channel) {
    // Learn listeners
    this.learnCCListeners.forEach(cb => {
      try { cb(controller, value, channel); }
      catch (e) { console.error('CC learn listener error:', e); }
    });

    // Registered handlers
    const handler = this.ccHandlers.get(controller);
    if (handler) {
      try { handler(controller, value, channel); }
      catch (e) { console.error('CC handler error:', e); }
    }

    // Rig → SignalBus in App; avoid duplicating into controlValues here
  }

  _fireNote(note, velocity, isNoteOn, channel) {
    // Learn listeners
    this.learnNoteListeners.forEach(cb => {
      try { cb(note, velocity, isNoteOn, channel); }
      catch (e) { console.error('Note learn listener error:', e); }
    });

    // Registered handlers
    const handler = this.noteHandlers.get(note);
    if (handler) {
      try { handler(note, velocity, isNoteOn, channel); }
      catch (e) { console.error('Note handler error:', e); }
    }

    // Rig → SignalBus in App
  }

  // Register/unregister CC handlers
  onCC(controller, handler) { this.ccHandlers.set(controller, handler); }
  offCC(controller) { this.ccHandlers.delete(controller); }

  // Register/unregister note handlers
  onNote(note, handler) { this.noteHandlers.set(note, handler); }
  offNote(note) { this.noteHandlers.delete(note); }

  // Learn mode
  addLearnCCListener(cb) { this.learnCCListeners.add(cb); }
  removeLearnCCListener(cb) { this.learnCCListeners.delete(cb); }
  addLearnNoteListener(cb) { this.learnNoteListeners.add(cb); }
  removeLearnNoteListener(cb) { this.learnNoteListeners.delete(cb); }
  clearLearnListeners() { this.learnCCListeners.clear(); this.learnNoteListeners.clear(); }

  destroy() {
    this.ccHandlers.clear();
    this.noteHandlers.clear();
    this.clearLearnListeners();
  }
}
